import { useState, useEffect, useRef } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi.ts';
import { useUserId } from '@/auth/UserIdContext.tsx';
import { logger } from '@/logger';
import type { StateSnapshot } from '@/types.ts';

import type { UserState } from '../../../../../bridge/types/state.ts';

const STORAGE_KEY = 'beacon_state_snapshots';

// Load snapshots from localStorage
const loadSnapshotsFromStorage = (): Record<string, StateSnapshot> => {
	const stored = localStorage.getItem(STORAGE_KEY);
	if (stored) {
		try {
			return JSON.parse(stored);
		} catch (e) {
			console.error('Failed to parse snapshots from localStorage:', e);
			return {};
		}
	}
	return {};
};

interface SnapshotManagerProps {
	currentUserState: UserState;
	onLoadSnapshot: (snapshot: StateSnapshot) => void;
}

export const SnapshotManager = ({
	currentUserState,
	onLoadSnapshot,
}: SnapshotManagerProps) => {
	const bridgewayApi = useBridgewayApi();
	const userId = useUserId();

	// Initialize state directly from localStorage
	const [snapshots, setSnapshots] = useState<Record<string, StateSnapshot>>(
		loadSnapshotsFromStorage,
	);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [editingName, setEditingName] = useState('');
	const [savingId, setSavingId] = useState<string | null>(null);
	const [loadingId, setLoadingId] = useState<string | null>(null);
	const [statusMessage, setStatusMessage] = useState<{
		text: string;
		error: boolean;
	} | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	// Save snapshots to localStorage whenever they change
	useEffect(() => {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshots));
	}, [snapshots]);

	const handleSaveSnapshot = async () => {
		const id = `snapshot_${Date.now()}`;
		const timestamp = Date.now();
		const name = `Snapshot ${new Date(timestamp).toLocaleString()}`;

		setSavingId(id);
		setStatusMessage(null);

		let cosmos: Record<string, unknown[]> | undefined;
		let elasticsearch: Record<string, unknown[]> | undefined;

		if (userId) {
			try {
				const data = await bridgewayApi.get<{
					cosmos: Record<string, unknown[]>;
					elasticsearch: Record<string, unknown[]>;
				}>(`/api/user/${userId}/snapshot`);
				cosmos = data.cosmos;
				elasticsearch = data.elasticsearch;
			} catch (err) {
				logger.error('Failed to fetch Cosmos/ES snapshot data', err);
				setStatusMessage({
					text: 'Warning: saved Redis state only (Cosmos/ES fetch failed)',
					error: true,
				});
			}
		}

		const newSnapshot: StateSnapshot = {
			id,
			name,
			timestamp,
			user_state: structuredClone(currentUserState),
			cosmos,
			elasticsearch,
		};

		setSnapshots((prev) => ({
			...prev,
			[id]: newSnapshot,
		}));

		setSavingId(null);
		if (!statusMessage) {
			const hasDatalayer = cosmos !== undefined || elasticsearch !== undefined;
			setStatusMessage({
				text: hasDatalayer
					? 'Saved (Redis + Cosmos + ES)'
					: 'Saved (Redis only)',
				error: false,
			});
			setTimeout(() => setStatusMessage(null), 3000);
		}
	};

	const handleDeleteSnapshot = (id: string) => {
		if (confirm('Are you sure you want to delete this snapshot?')) {
			setSnapshots((prev) => {
				const newSnapshots = { ...prev };
				delete newSnapshots[id];
				return newSnapshots;
			});
		}
	};

	const handleStartRename = (snapshot: StateSnapshot) => {
		setEditingId(snapshot.id);
		setEditingName(snapshot.name);
	};

	const handleSaveRename = (id: string) => {
		if (editingName.trim()) {
			setSnapshots((prev) => ({
				...prev,
				[id]: {
					...prev[id],
					name: editingName.trim(),
				},
			}));
		}
		setEditingId(null);
		setEditingName('');
	};

	const handleCancelRename = () => {
		setEditingId(null);
		setEditingName('');
	};

	const handleLoadSnapshot = async (snapshot: StateSnapshot) => {
		if (
			!confirm(
				'Are you sure you want to load this snapshot? This will replace the current state.',
			)
		) {
			return;
		}

		// Always restore Redis state via bridge action
		onLoadSnapshot(snapshot);

		// Restore Cosmos + ES if the snapshot includes that data
		if (userId && (snapshot.cosmos || snapshot.elasticsearch)) {
			setLoadingId(snapshot.id);
			setStatusMessage(null);
			try {
				await bridgewayApi.put(`/api/user/${userId}/snapshot`, {
					cosmos: snapshot.cosmos ?? {},
					elasticsearch: snapshot.elasticsearch ?? {},
				});
				setStatusMessage({
					text: 'Loaded (Redis + Cosmos + ES)',
					error: false,
				});
			} catch (err) {
				logger.error('Failed to restore Cosmos/ES snapshot data', err);
				setStatusMessage({
					text: 'Loaded Redis only (Cosmos/ES restore failed)',
					error: true,
				});
			} finally {
				setLoadingId(null);
				setTimeout(() => setStatusMessage(null), 3000);
			}
		}
	};

	const handleViewPrompt = (snapshot: StateSnapshot) => {
		window.open(`/demo/prompt-viewer?snapshot=${snapshot.id}`, '_blank');
	};

	const handleExportSnapshot = (snapshot: StateSnapshot) => {
		// Convert snapshot to JSON string
		const jsonString = JSON.stringify(snapshot, null, 2);

		// Create blob and download
		const blob = new Blob([jsonString], { type: 'application/json' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');

		// Generate filename with sanitized name and timestamp
		const sanitizedName = snapshot.name
			.replace(/[^a-z0-9]/gi, '_')
			.toLowerCase();
		link.download = `snapshot-${sanitizedName}-${snapshot.timestamp}.json`;
		link.href = url;

		// Trigger download and cleanup
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	const handleImportSnapshot = () => {
		// Trigger file input click
		fileInputRef.current?.click();
	};

	const handleFileChange = async (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const file = event.target.files?.[0];
		if (!file) return;

		try {
			// Read file contents
			const text = await file.text();
			const importedSnapshot = JSON.parse(text) as StateSnapshot;

			// Validate required fields
			if (
				!importedSnapshot.id ||
				!importedSnapshot.name ||
				typeof importedSnapshot.timestamp !== 'number' ||
				!importedSnapshot.user_state
			) {
				alert('Invalid snapshot file: Missing required fields');
				return;
			}

			// Generate new unique ID to prevent conflicts
			const newId = `snapshot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

			// Update snapshot with new ID and mark as imported
			const newSnapshot: StateSnapshot = {
				...importedSnapshot,
				id: newId,
				name: `${importedSnapshot.name} (imported)`,
				timestamp: Date.now(), // Update to import time
			};

			// Add to snapshots
			setSnapshots((prev) => ({
				...prev,
				[newId]: newSnapshot,
			}));

			alert(`Successfully imported snapshot: ${newSnapshot.name}`);
		} catch (error) {
			console.error('Failed to import snapshot:', error);
			if (error instanceof SyntaxError) {
				alert('Invalid snapshot file: File is not valid JSON');
			} else {
				alert(
					`Failed to import snapshot: ${error instanceof Error ? error.message : 'Unknown error'}`,
				);
			}
		} finally {
			// Reset file input so the same file can be imported again
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
		}
	};

	const snapshotList = Object.values(snapshots).sort(
		(a, b) => b.timestamp - a.timestamp,
	);

	const isBusy = savingId !== null || loadingId !== null;

	return (
		<div
			className="flex flex-1 flex-col overflow-hidden rounded-lg bg-white
				shadow"
		>
			{/* Header */}
			<div className="border-b border-gray-200 bg-gray-50 p-4">
				<h2 className="text-lg font-semibold">Snapshots</h2>
				<p className="mt-1 text-xs text-gray-600">
					{snapshotList.length} saved
				</p>
			</div>

			{/* Save and Import buttons */}
			<div className="border-b border-gray-200 p-4">
				<button
					onClick={handleSaveSnapshot}
					disabled={isBusy}
					className="mb-2 w-full rounded bg-green-600 px-4 py-2 text-sm
						font-medium text-white transition hover:bg-green-700
						disabled:cursor-not-allowed disabled:opacity-60"
				>
					{savingId ? 'Saving…' : 'Save Current State'}
				</button>
				<button
					onClick={handleImportSnapshot}
					disabled={isBusy}
					className="w-full rounded bg-orange-600 px-4 py-2 text-sm font-medium
						text-white transition hover:bg-orange-700
						disabled:cursor-not-allowed disabled:opacity-60"
				>
					Import Snapshot
				</button>
				<input
					ref={fileInputRef}
					type="file"
					accept=".json"
					onChange={handleFileChange}
					className="hidden"
				/>
				{statusMessage && (
					<p
						className={`mt-2 text-xs
						${statusMessage.error ? 'text-red-600' : 'text-green-700'}`}
					>
						{statusMessage.text}
					</p>
				)}
			</div>

			{/* Snapshot list */}
			<div className="flex-1 overflow-y-auto">
				{snapshotList.length === 0 ? (
					<div className="p-4 text-center text-sm text-gray-500">
						No snapshots yet. Save your current state to get started.
					</div>
				) : (
					<div className="divide-y divide-gray-200">
						{snapshotList.map((snapshot) => (
							<div key={snapshot.id} className="p-4 hover:bg-gray-50">
								{/* Snapshot name */}
								{editingId === snapshot.id ? (
									<div className="mb-2 flex items-center gap-2">
										<input
											type="text"
											value={editingName}
											onChange={(e) => setEditingName(e.target.value)}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													handleSaveRename(snapshot.id);
												} else if (e.key === 'Escape') {
													handleCancelRename();
												}
											}}
											className="flex-1 rounded border border-gray-300 px-2 py-1
												text-sm focus:border-blue-500 focus:ring-2
												focus:ring-blue-200 focus:outline-none"
											autoFocus
										/>
										<button
											onClick={() => handleSaveRename(snapshot.id)}
											className="text-green-600 hover:text-green-700"
										>
											✓
										</button>
										<button
											onClick={handleCancelRename}
											className="text-red-600 hover:text-red-700"
										>
											✕
										</button>
									</div>
								) : (
									<div className="mb-1 font-medium text-gray-900">
										{snapshot.name}
										{(snapshot.cosmos || snapshot.elasticsearch) && (
											<span className="ml-1 text-xs font-normal text-gray-400">
												(+data layer)
											</span>
										)}
									</div>
								)}

								{/* Timestamp */}
								<div className="mb-3 text-xs text-gray-500">
									{new Date(snapshot.timestamp).toLocaleString()}
								</div>

								{/* Actions */}
								<div className="flex flex-wrap gap-2">
									<button
										onClick={() => handleLoadSnapshot(snapshot)}
										disabled={isBusy}
										className="rounded bg-blue-600 px-3 py-1 text-xs text-white
											transition hover:bg-blue-700 disabled:cursor-not-allowed
											disabled:opacity-60"
									>
										{loadingId === snapshot.id ? 'Loading…' : 'Load'}
									</button>
									<button
										onClick={() => handleViewPrompt(snapshot)}
										className="rounded bg-purple-600 px-3 py-1 text-xs
											text-white transition hover:bg-purple-700"
									>
										View Prompt
									</button>
									<button
										onClick={() => handleExportSnapshot(snapshot)}
										className="rounded bg-teal-600 px-3 py-1 text-xs text-white
											transition hover:bg-teal-700"
									>
										Export
									</button>
									<button
										onClick={() => handleStartRename(snapshot)}
										className="rounded bg-gray-600 px-3 py-1 text-xs text-white
											transition hover:bg-gray-700"
									>
										Rename
									</button>
									<button
										onClick={() => handleDeleteSnapshot(snapshot.id)}
										className="rounded bg-red-600 px-3 py-1 text-xs text-white
											transition hover:bg-red-700"
									>
										Delete
									</button>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};
